'use client'

import { chatPrefillMark } from '@/app/lib/guideChat/prefill'
import type { CollectedFieldName } from '@/app/lib/guideChat/types'

import { forwardRef, useImperativeHandle, useEffect, useMemo, useState, useRef } from 'react'
import { useForm, Controller, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@/app/lib/zodResolver'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import {
    borrowerGuaranteesFormSchema,
    BorrowerGuaranteesFormData,
    createBorrowerGuaranteesDefaults,
    GuaranteeOptions,
    GUARANTEE_DC,
    GUARANTEE_PTIA,
    GUARANTEE_IPT_ITT,
    GUARANTEE_IPP,
    GUARANTEE_PLUS,
    GuaranteeCode,
} from '@/app/schemas/loan-comparator/guarantees.schema'
import {
    GUARANTEES_DATA as FALLBACK_GUARANTEES_DATA,
    GUARANTEE_DISPLAY_ORDER,
    GUARANTEE_LABELS,
    GUARANTEE_SHORT_DESCRIPTIONS,
    PROJECT_PURPOSE_RESIDENTIAL,
    PROJECT_PURPOSE_RENTAL,
    PROJECT_PURPOSE_SCPI,
    PROJECT_PURPOSE_PROFESSIONAL,
    RETIRED_JOB_ID,
    UNEMPLOYED_JOB_ID,
    needsSwitching,
    LOAN_INFORMATION_TYPES,
} from '@/app/constants/loanTypes'
import { computeLoanMonthsLeft } from '@/app/utils/loanCalculations'
import { computeLoansTotalAmount } from '@/app/utils/loan/loan-insured-amount'
import AppCheckbox from '@/app/components/atomic/Input/AppCheckbox'
import AppFormInput from '@/app/components/atomic/AppFormInput'
import YesNoSwitch from '@/app/components/atomic/molecules/YesNoSwitch'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import LoanImportedBadge from '@/app/components/loanComparator/pdfExtract/LoanImportedBadge'
import { useLoanPdfImport } from '@/app/contexts/loan-pdf-import-context'
import { borrowerFieldKey } from '@/app/utils/loan/loan-pdf-extract-merge'
import { IGuaranteeData } from '@/app/models/loanQuote'
import { convertOptionsObjectToArray } from '@/app/utils/loan/borrower-mapper'
import { getBankRequiredGuarantees } from '@/app/utils/loan/guarantees'
import { parseDDMMYYYYOrISODate } from '@/app/utils/helpers'

// Exposed methods interface
export interface BorrowerGuaranteesFormHandle {
    validate: () => Promise<boolean>
    getData: () => BorrowerGuaranteesFormData
    reset: () => void
    setCoverageError: (message?: string) => void
    getErrorMessages: () => string[]
}

interface Props {
    isMainBorrower?: boolean
    className?: string
    onCoverageBlur?: () => void
    externalCoverageError?: string | null
}

const LoanComparatorBorrowerGuaranteesForm = forwardRef<BorrowerGuaranteesFormHandle, Props>(
    ({ isMainBorrower = true, className = '', onCoverageBlur, externalCoverageError }, ref) => {
        // Store state - select bank_name directly for reactivity
        const quote = useLoanComparatorStore((state) => state.quote)
        const quoteBankName = useLoanComparatorStore((state) => state.quote?.bank_name)
        const globalOptions = useLoanComparatorStore((state) => state.globalOptions)
        const borrowerActions = useLoanComparatorStore((state) => state.borrower)

        // Get borrower data
        const borrowerData = useMemo(() => {
            return isMainBorrower ? quote?.main_borrower : quote?.co_borrower
        }, [quote, isMainBorrower])

        // Get project purpose from quote (default to residential)
        // IMPORTANT: Backend returns type_insurance.id, but form updates project_purpose
        // We need to check both sources to handle initial load AND form changes
        const projectPurpose = useMemo(() => {
            // First check project_purpose (set by form changes)
            // Then fallback to type_insurance.id (from backend)
            // Finally fallback to residential default
            return quote?.project_purpose ?? quote?.type_insurance?.id ?? PROJECT_PURPOSE_RESIDENTIAL
        }, [quote?.project_purpose, quote?.type_insurance?.id])

        // Get raw bank name for API lookups (uses direct selector for reactivity)
        const bankName = quoteBankName || null

        // Get bank denomination for display (with proper article: "le" or "la")
        const bankDenomination = useMemo(() => {
            // If no bank or "Autre" / "Je ne sais pas"
            if (!quoteBankName || quoteBankName === 'Autre' || quoteBankName === 'Je ne sais pas') {
                return 'la banque'
            }

            const firstWord = quoteBankName.split(' ')[0].toLowerCase()

            // Banks that use "le"
            if (['crédit', 'cic'].includes(firstWord)) {
                return `le ${quoteBankName}`
            }

            // Banks that use "la"
            if (['caisse', 'bnp', 'banque', 'société'].includes(firstWord)) {
                return `la ${quoteBankName}`
            }

            // Default: just the bank name
            return quoteBankName
        }, [quoteBankName])

        // Check if borrower is retired
        // Note: The form stores job as { label, value } in borrowerData.job
        // type_work is used when loading from API, job is used when form updates
        const isRetired = useMemo(() => {
            // Check both possible sources of job ID
            const jobId = borrowerData?.job?.value ?? borrowerData?.type_work?.id
            return jobId === RETIRED_JOB_ID
        }, [borrowerData?.job?.value, borrowerData?.type_work?.id])

        // Check if borrower is unemployed
        const isUnemployed = useMemo(() => {
            // Check both possible sources of job ID
            const jobId = borrowerData?.job?.value ?? borrowerData?.type_work?.id
            return jobId === UNEMPLOYED_JOB_ID
        }, [borrowerData?.job?.value, borrowerData?.type_work?.id])

        // Check if borrower is an "old borrower" (age >= maxAgeForMoreThanDCOption)
        const isOldBorrower = useMemo(() => {
            const birthdate = borrowerData?.prospect_info?.birthday_date
            if (!birthdate) return false

            const maxAge = globalOptions?.maxAgeForMoreThanDCOption || 70

            // Le store peut contenir la date au format DD/MM/YYYY (saisie) ou ISO (API)
            const birthDate = parseDDMMYYYYOrISODate(birthdate)
            if (!birthDate) return false

            const today = new Date()
            const age = Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))

            return age >= maxAge
        }, [borrowerData?.prospect_info?.birthday_date, globalOptions?.maxAgeForMoreThanDCOption])

        // Check if borrower is expat
        const isExpat = useMemo(() => {
            return borrowerData?.is_expat || false
        }, [borrowerData?.is_expat])

        // Get borrower's country
        // In Vue, address.country is mapped from prospect_info.address_sub_area or address_country
        const borrowerCountry = useMemo(() => {
            return borrowerData?.prospect_info?.address_sub_area || borrowerData?.prospect_info?.address_country || null
        }, [borrowerData?.prospect_info?.address_sub_area, borrowerData?.prospect_info?.address_country])

        // Check if expat is in an allowed country (DOM-TOM)
        // Expats in allowed countries can get IPT/ITT guarantees as optional
        // Expats NOT in allowed countries cannot get IPT/ITT at all
        const isExpatInAllowedCountry = useMemo(() => {
            if (!isExpat || !borrowerCountry) return false
            const allowedCountries = globalOptions?.expatAllowedCountries || []
            return allowedCountries.includes(borrowerCountry)
        }, [isExpat, borrowerCountry, globalOptions?.expatAllowedCountries])

        // Get guarantees data from globalOptions or fallback
        const guaranteesData = useMemo((): Record<string, IGuaranteeData> => {
            if (globalOptions?.bankGuaranteesData) {
                return globalOptions.bankGuaranteesData
            }
            // Fallback to hardcoded data if API not loaded
            return FALLBACK_GUARANTEES_DATA as unknown as Record<string, IGuaranteeData>
        }, [globalOptions?.bankGuaranteesData])

        // Get required guarantees based on bank, project purpose, and borrower status
        // This mirrors Symfony's requiredGuarantees getter logic
        const requiredGuarantees = useMemo((): GuaranteeCode[] => {
            const requiredCodes = getBankRequiredGuarantees(globalOptions, bankName, projectPurpose, isOldBorrower)

            // Filter guarantees based on borrower conditions
            const filteredGuarantees = Object.values(guaranteesData).filter((g) => {
                // Check old borrower condition
                if (g.conditions?.oldBorrower === false && isOldBorrower) {
                    return false
                }

                // Check expat condition
                if (isExpat && !g.conditions?.expat) {
                    return false
                }

                // Check retired condition
                if (g.conditions?.retired === false && isRetired) {
                    return false
                }

                // Check unemployed condition
                if (g.conditions?.unemployed === false && isUnemployed) {
                    return false
                }

                // Check if in required codes
                if (!requiredCodes.includes(g.code)) {
                    return false
                }

                return true
            })

            // L'ordre de Object.values(guaranteesData) vient de l'API : tri explicite pour l'affichage
            return filteredGuarantees
                .map((g) => g.code as GuaranteeCode)
                .sort((a, b) => GUARANTEE_DISPLAY_ORDER.indexOf(a) - GUARANTEE_DISPLAY_ORDER.indexOf(b))
        }, [globalOptions, bankName, projectPurpose, guaranteesData, isOldBorrower, isExpat, isRetired, isUnemployed])

        // Optional guarantees (not required but can be selected)
        // In Symfony, optional guarantees are ALL guarantees not in requiredGuarantees
        // They are displayed but may be disabled based on borrower conditions
        const optionalGuarantees = useMemo((): GuaranteeCode[] => {
            const required = new Set(requiredGuarantees)

            // Get all guarantee codes that are NOT in required
            const allGuarantees = Object.values(guaranteesData).map((g) => g.code as GuaranteeCode)

            return allGuarantees
                .filter((g) => !required.has(g))
                .sort((a, b) => GUARANTEE_DISPLAY_ORDER.indexOf(a) - GUARANTEE_DISPLAY_ORDER.indexOf(b))
        }, [requiredGuarantees, guaranteesData])

        // Dynamic message explaining why these guarantees are required
        // Mirrors Symfony's formattedGuaranteesPhrase getter - ORDER IS IMPORTANT
        const formattedGuaranteesPhrase = useMemo((): string | null => {
            // 1. Expat in NON-allowed country (checked FIRST - highest priority)
            // These expats cannot get IPT/ITT guarantees at all
            if (isExpat && !isExpatInAllowedCountry) {
                return "En tant qu'expatrié, vous ne pouvez pas bénéficier des garanties IPT (invalidité) et ITT (arrêt de travail)."
            }

            // 2. Old borrower (age-based) - only DC guarantee
            if (isOldBorrower) {
                return 'Etant donné votre âge, vous pourrez uniquement prétendre à la garantie Décès.'
            }

            // 3. Retired - every guarantee stays available, the insurers' age limits decide (Bank.php `retired => true`)
            if (isRetired) {
                return "En tant que retraité, les garanties Décès, PTIA, IPT/ITT, IPP et Dos/Psy peuvent vous être proposées (sous réserve des conditions d'âge des assureurs)."
            }

            // 4. Unemployed - IPT, IPP, ITT and Dos/Psy not applicable
            if (isUnemployed) {
                return 'Comme vous ne travaillez pas, les garanties IPT, IPP, ITT et Dos/Psy ne vous sont pas applicables'
            }

            // 5. Expat in an ALLOWED country
            // These expats can get IPT/ITT as optional guarantees
            if (isExpat && isExpatInAllowedCountry) {
                return `En tant qu'expatrié, seules les garanties DC/PTIA seront exigées par votre banque. Vous pouvez bénéficier de garanties complémentaires si vous le souhaitez.`
            }

            // 6. Check project purpose
            if (projectPurpose === PROJECT_PURPOSE_SCPI) {
                return 'En cas de SCPI, seules les garanties Décès et PTIA sont obligatoires'
            }

            if (projectPurpose === PROJECT_PURPOSE_RENTAL) {
                return "En cas d'investissement locatif, seules les garanties Décès et PTIA sont obligatoires"
            }

            if (projectPurpose === PROJECT_PURPOSE_PROFESSIONAL) {
                return 'Pour un prêt professionnel, seules les garanties Décès et PTIA sont obligatoires'
            }

            // 7. For residential with a valid bank
            // Note: In Vue/Symfony there's a bug at line 893 where "this.requiredGuaranteeCodes" is used
            // instead of "requiredGuaranteeCodes", making the PLUS-only check always false.
            // We reproduce this behavior for consistency.
            if (
                projectPurpose === PROJECT_PURPOSE_RESIDENTIAL &&
                bankName &&
                bankName !== 'Autre' &&
                bankName !== 'Je ne sais pas'
            ) {
                const requiredCodes = requiredGuarantees

                // Check for additional guarantees beyond DC/PTIA/IPT_ITT
                const hasIPP = requiredCodes.includes(GUARANTEE_IPP)
                const hasPLUS = requiredCodes.includes(GUARANTEE_PLUS)

                // Only show custom message if IPP is required (Vue bug: PLUS-only case falls through to default)
                if (hasIPP && hasPLUS) {
                    return `En cas d'achat résidentiel avec ${bankDenomination}, nous avons sélectionné les garanties IPP et Dos/Psy afin que le contrat d'assurance soit accepté par ${bankDenomination}`
                }

                if (hasIPP) {
                    return `En cas d'achat résidentiel avec ${bankDenomination}, nous avons sélectionné la garantie IPP afin que le contrat d'assurance soit accepté par ${bankDenomination}`
                }

                // Note: Vue has a bug where PLUS-only check uses "this.requiredGuaranteeCodes" which is undefined
                // So the PLUS-only message is never shown, and it falls through to the default message below
                // We intentionally omit the "if (hasPLUS)" check to match Vue's behavior
            }

            // Divergence assumée avec Symfony : le message résidentiel par défaut répète le tag « Exigée par la banque »
            return null
        }, [
            isRetired,
            isUnemployed,
            isOldBorrower,
            isExpat,
            isExpatInAllowedCountry,
            projectPurpose,
            bankName,
            bankDenomination,
            requiredGuarantees,
        ])

        // Whether to display the bank/expat image in the guarantees alert
        const displayAlertImage = useMemo(() => {
            if (isExpat) return true
            return !isOldBorrower && !isRetired && !isUnemployed
        }, [isExpat, isOldBorrower, isRetired, isUnemployed])

        // Get the image source for the guarantees alert
        const alertImageSrc = useMemo(() => {
            if (isExpat) {
                return '/images/loanComparator/expatrie.svg'
            }
            if (!bankName || bankName === 'Autre' || bankName === 'Je ne sais pas') {
                return '/images/banks/bank_icon.png'
            }
            const normalized = bankName.replace(/ /g, '_').replace(/é/g, 'e').replace(/'/g, '_').toLowerCase()
            return `/images/banks/${normalized}.png`
        }, [isExpat, bankName])

        // Check if an optional guarantee can be selected (matches Symfony's optionFillRequirements)
        const optionFillRequirements = (code: GuaranteeCode): boolean => {
            const guarantee = guaranteesData[code]
            if (!guarantee) return false

            let requirementsAreFilled = true

            // Check old borrower condition
            if (guarantee.conditions?.oldBorrower === false && isOldBorrower) {
                requirementsAreFilled = false
            }

            // Check retired condition
            if (guarantee.conditions?.retired === false && isRetired) {
                requirementsAreFilled = false
            }

            // Check unemployed condition
            if (guarantee.conditions?.unemployed === false && isUnemployed) {
                requirementsAreFilled = false
            }

            // Check expat condition: a guarantee also stays selectable in the countries it lists (parité Vue)
            const allowsExpats =
                guarantee.conditions?.expat || (!!borrowerCountry && guarantee.conditions?.countryList?.includes(borrowerCountry))
            if (isExpat && !allowsExpats) {
                requirementsAreFilled = false
            }

            return requirementsAreFilled
        }

        // Local state for dependency alerts
        const [showDependencyAlert, setShowDependencyAlert] = useState<Record<string, boolean>>({})

        // State for confirmation modal (when unchecking a required guarantee)
        const [confirmModal, setConfirmModal] = useState<{
            isOpen: boolean
            guaranteeCode: GuaranteeCode | null
            guaranteeName: string
        }>({
            isOpen: false,
            guaranteeCode: null,
            guaranteeName: '',
        })

        // Get initial values from borrower data
        const initialValues = useMemo((): BorrowerGuaranteesFormData => {
            if (borrowerData) {
                // Convert borrower options array to object
                // IMPORTANT: Backend returns options in lowercase: ['dc', 'ptia', 'ipt', 'ipp', 'plus']
                // Note: 'ipt' maps to IPT_ITT (VueJS uses 'ipt' not 'ipt_itt')
                const optionsArray = borrowerData.options || []
                const options: GuaranteeOptions = {
                    [GUARANTEE_DC]: optionsArray.includes('dc'),
                    [GUARANTEE_PTIA]: optionsArray.includes('ptia'),
                    [GUARANTEE_IPT_ITT]: optionsArray.includes('ipt'), // Backend uses 'ipt' for IPT_ITT
                    [GUARANTEE_IPP]: optionsArray.includes('ipp'),
                    [GUARANTEE_PLUS]: optionsArray.includes('plus'),
                }

                return {
                    cover_percentage: borrowerData.proportion || 100,
                    options,
                    has_side_loans: borrowerData.side_loans || false,
                    is_lemoine: borrowerData.outstanding_total_credit_under_lemoine_limit ?? false,
                }
            }
            return createBorrowerGuaranteesDefaults(projectPurpose)
        }, [borrowerData, projectPurpose])

        const formatErrors = (err: FieldErrors): string[] => {
            return Object.entries(err).flatMap(([key, value]) => {
                if (!value) return []
                if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
                    return [`${key}: ${value.message}`]
                }
                if (typeof value === 'object') {
                    return formatErrors(value as FieldErrors).map((child) => `${key}.${child}`)
                }
                return []
            })
        }

        // Setup React Hook Form
        const {
            register,
            control,
            formState: { errors },
            watch,
            trigger,
            getValues,
            reset: resetForm,
            setError,
            clearErrors,
            setValue,
        } = useForm<BorrowerGuaranteesFormData>({
            resolver: zodResolver(borrowerGuaranteesFormSchema),
            mode: 'onBlur',
            defaultValues: initialValues,
        })

        // Expose methods to parent
        useImperativeHandle(ref, () => ({
            validate: async () => {
                return await trigger()
            },
            getData: () => {
                return getValues()
            },
            reset: () => {
                resetForm(createBorrowerGuaranteesDefaults(projectPurpose))
            },
            setCoverageError: (message?: string) => {
                if (message) {
                    setError('cover_percentage', { type: 'manual', message })
                } else {
                    clearErrors('cover_percentage')
                }
            },
            getErrorMessages: () => {
                return formatErrors(errors)
            },
        }))

        // Reset form when borrower data changes (e.g., when navigating back from results page or initial load)
        // Excludes proportion: changing the quotité must not reset the guarantees (Symfony parity)
        const borrowerDataString = JSON.stringify({ id: borrowerData?.id })
        const prevDataStringRef = useRef<string | undefined>(undefined)

        useEffect(() => {
            // Reset if we have data and it's different from previous
            if (borrowerData && borrowerDataString !== prevDataStringRef.current) {
                resetForm(initialValues)
                prevDataStringRef.current = borrowerDataString
            }
        }, [borrowerDataString, borrowerData, initialValues, resetForm])

        // Watch values
        const coverPercentage = watch('cover_percentage')
        const options = watch('options')
        const hasSideLoans = watch('has_side_loans')

        // Get current job ID for tracking changes
        // Track the render count to distinguish first render from subsequent ones
        const renderCountRef = useRef(0)
        // Track previous bank name to detect bank changes (like Vue.js implicit watcher)
        const prevBankNameRef = useRef<string | null>(bankName)
        // Track if we've done the initial setup
        const hasInitializedRef = useRef(false)

        // Helper function to update guarantees (matches Vue.js updateGuarantees method)
        const updateGuarantees = () => {
            // First, uncheck ALL guarantees
            const allGuaranteeCodes: GuaranteeCode[] = [
                GUARANTEE_DC,
                GUARANTEE_PTIA,
                GUARANTEE_IPT_ITT,
                GUARANTEE_IPP,
                GUARANTEE_PLUS,
            ]
            allGuaranteeCodes.forEach((code) => {
                setValue(`options.${code}`, false)
            })

            // Then, check only the required guarantees
            requiredGuarantees.forEach((code) => {
                setValue(`options.${code}`, true)
            })
        }

        // Effect for initialization (matches Vue.js mounted -> initGuarantees)
        useEffect(() => {
            if (hasInitializedRef.current) return
            hasInitializedRef.current = true

            // initGuarantees() - only set guarantees if none are selected
            const currentOptions = getValues('options')
            const selectedGuarantees = Object.values(currentOptions || {}).filter((v) => v === true)

            if (selectedGuarantees.length < 1) {
                // No guarantees selected - set the required ones
                requiredGuarantees.forEach((code) => {
                    setValue(`options.${code}`, true)
                })
            }
        }, [requiredGuarantees, setValue, getValues])

        // Dos/Psy est exigée par toutes les banques dès que l'ITT est couverte, quelle que soit la map
        // bankRequiredGuarantees du backend. On l'applique à la transition de l'ITT et non à son état,
        // pour couvrir l'hydratation, l'init, le changement de banque et l'import PDF sans empêcher
        // l'utilisateur de la retirer ensuite.
        const previousIptRef = useRef<boolean | undefined>(undefined)
        useEffect(() => {
            const iptChecked = !!options?.[GUARANTEE_IPT_ITT]
            const wasChecked = previousIptRef.current
            previousIptRef.current = iptChecked

            if (!iptChecked || wasChecked) return
            if (options?.[GUARANTEE_PLUS] || !optionFillRequirements(GUARANTEE_PLUS)) return

            setValue(`options.${GUARANTEE_PLUS}`, true)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [options?.[GUARANTEE_IPT_ITT]])

        // Effect for bank name changes (matches Vue.js behavior where bank change triggers guarantee update)
        // In Vue.js, the requiredGuarantees getter depends on bank, so changing bank recalculates it
        // and triggers the watcher even if the result is the same (because it's a new array reference)
        useEffect(() => {
            renderCountRef.current += 1

            // Skip the first render (handled by initialization effect)
            if (renderCountRef.current === 1) {
                prevBankNameRef.current = bankName
                return
            }

            // Check if bank actually changed
            if (prevBankNameRef.current === bankName) {
                return
            }

            prevBankNameRef.current = bankName
            updateGuarantees()
        }, [bankName, requiredGuarantees, setValue])

        // Effect for project purpose changes (already handled via requiredGuarantees dependency)
        const prevProjectPurposeRef = useRef(projectPurpose)
        useEffect(() => {
            // Skip if project purpose hasn't changed
            if (prevProjectPurposeRef.current === projectPurpose) {
                return
            }

            prevProjectPurposeRef.current = projectPurpose
            updateGuarantees()
        }, [projectPurpose, requiredGuarantees, setValue])

        const { isImported, clearImported } = useLoanPdfImport()
        const importSide = isMainBorrower ? 'main' : 'co'
        const prefilledByChat = useLoanComparatorStore((state) => state.prefilledByChat)
        const quotityChatMark = chatPrefillMark(prefilledByChat, isMainBorrower ? 'proportionMain' : 'proportionCo')

        // Sync form changes to store (excluding cover_percentage which syncs on blur only)
        useEffect(() => {
            const subscription = watch((formData, { name }) => {
                if (!formData.options) return

                // Skip cover_percentage changes - it syncs on blur only to avoid lag
                if (name === 'cover_percentage') return

                const selectedOptions = convertOptionsObjectToArray(formData.options as Record<string, boolean>)

                const updateFn = isMainBorrower ? borrowerActions.updateMain : borrowerActions.updateCo
                updateFn({
                    options: selectedOptions,
                    side_loans: formData.has_side_loans,
                    outstanding_total_credit_under_lemoine_limit: formData.is_lemoine,
                })
            })
            return () => subscription.unsubscribe()
        }, [watch, borrowerActions, isMainBorrower])

        // Sync cover_percentage to store on blur only (to avoid lag while typing)
        const syncCoverPercentageToStore = () => {
            const coverPercentageValue = getValues('cover_percentage')
            const updateFn = isMainBorrower ? borrowerActions.updateMain : borrowerActions.updateCo
            updateFn({
                proportion: coverPercentageValue,
            })
        }

        // DC/PTIA (uncheckableIfRequired: false) sont verrouillées quand la banque les exige, sauf en
        // investissement locatif : la banque les exige mais elles restent facultatives chez nous, donc
        // décochables après confirmation comme IPT/ITT.
        const isLockedWhenRequired = (code: GuaranteeCode): boolean =>
            projectPurpose !== PROJECT_PURPOSE_RENTAL && guaranteesData[code]?.uncheckableIfRequired === false

        // Handle guarantee toggle - matches Symfony's updateOption/updateOptionValue logic
        const handleGuaranteeToggle = (code: GuaranteeCode, checked: boolean) => {
            const guarantee = guaranteesData[code]
            if (!guarantee) return

            // Reset dependency alerts
            setShowDependencyAlert({})

            // Check if this is a required guarantee
            const isRequired = requiredGuarantees.includes(code)

            // For required guarantees that are being unchecked
            if (isRequired && !checked) {
                if (isLockedWhenRequired(code)) {
                    return
                }
                // Show confirmation modal before allowing uncheck
                setConfirmModal({
                    isOpen: true,
                    guaranteeCode: code,
                    guaranteeName: GUARANTEE_LABELS[code] ?? guarantee.name,
                })
                return // Don't uncheck yet, wait for confirmation
            }

            // Process the toggle (for non-required guarantees or when enabling)
            processGuaranteeToggle(code, checked)
        }

        // Process the actual guarantee toggle after any confirmation
        const processGuaranteeToggle = (code: GuaranteeCode, checked: boolean) => {
            // Handle dependencies based on guarantee conditions
            const hasDependants = code === GUARANTEE_IPT_ITT // IPT_ITT is required for IPP and PLUS

            // If disabling a guarantee that has dependants (IPT_ITT)
            if (!checked && hasDependants) {
                // A complementary guarantee cannot outlive the IPT_ITT it extends, bank-required or not
                const dependants: GuaranteeCode[] = [GUARANTEE_IPP, GUARANTEE_PLUS]
                dependants.forEach((dep) => {
                    if (options?.[dep]) {
                        setShowDependencyAlert((prev) => ({ ...prev, [dep]: true }))
                        setValue(`options.${dep}` as const, false)
                    }
                })
            }

            // Every bank requires Dos/Psy, so selecting it pulls in the IPT_ITT it depends on
            if (checked && code === GUARANTEE_PLUS && !options[GUARANTEE_IPT_ITT]) {
                if (!optionFillRequirements(GUARANTEE_IPT_ITT)) {
                    setShowDependencyAlert((prev) => ({ ...prev, [code]: true }))
                    return
                }
                setValue(`options.${GUARANTEE_IPT_ITT}`, true)
                clearImported(borrowerFieldKey(importSide, `options.${GUARANTEE_IPT_ITT}`))
            }

            // IPP stays an add-on: it cannot be selected without IPT_ITT unless the bank requires it
            if (
                checked &&
                code === GUARANTEE_IPP &&
                !options[GUARANTEE_IPT_ITT] &&
                !requiredGuarantees.includes(code)
            ) {
                setShowDependencyAlert((prev) => ({ ...prev, [code]: true }))
                return
            }

            setValue(`options.${code}`, checked)
            // Après les sorties anticipées : une garantie refusée reste celle du document
            clearImported(borrowerFieldKey(importSide, `options.${code}`))
        }

        // Handle confirmation modal actions
        const handleConfirmUncheck = () => {
            if (confirmModal.guaranteeCode) {
                processGuaranteeToggle(confirmModal.guaranteeCode, false)
            }
            setConfirmModal({ isOpen: false, guaranteeCode: null, guaranteeName: '' })
        }

        const handleCancelUncheck = () => {
            setConfirmModal({ isOpen: false, guaranteeCode: null, guaranteeName: '' })
        }

        // Une garantie exigée n'est grisée que si elle est verrouillée (voir isLockedWhenRequired)
        const isRequiredGuaranteeDisabled = isLockedWhenRequired

        // Une garantie facultative n'est grisée que par le profil emprunteur (retraité, expatrié…).
        // La dépendance IPP / Dos/Psy → IPT/ITT se joue au clic (alerte), pas en grisant : sur un
        // investissement locatif, une carte grisée se lit « indisponible » alors qu'elle est facultative.
        const isOptionalGuaranteeDisabled = (code: GuaranteeCode): boolean => !optionFillRequirements(code)

        // Lemoine constants (from API or fallback)
        const LEMOINE_AMOUNT_LIMIT = globalOptions?.lemoineThreshold?.amount || 200000
        const LEMOINE_AGE_LIMIT = globalOptions?.lemoineThreshold?.age || 60

        const isSwitchingQuote = needsSwitching(quote?.type_quote)

        const loansSignatureDate = quote?.loans?.[0]?.new_date_loan || quote?.date_contract_begin || null

        // Changement d'assurance : l'éligibilité Lemoine s'évalue sur le restant dû, jamais le montant initial
        const loansTotalAmount = useMemo(
            () => computeLoansTotalAmount(quote?.loans, loansSignatureDate, isSwitchingQuote),
            [quote?.loans, isSwitchingQuote, loansSignatureDate]
        )

        // Calculate max loan duration in months (using 'period' from ILoan)
        const loansTotalDurationInMonths = useMemo(() => {
            return Math.max(...(quote?.loans?.map((loan) => loan.period || 0) || [0]))
        }, [quote?.loans])

        // Durée restante max (mois) — saisie en mode « capital restant dû », sinon recalcul système depuis la
        // durée initiale (différé compris par convention) : un month_left du store peut être périmé après un switch
        const loansMaxMonthLeft = useMemo(() => {
            return Math.max(
                ...(quote?.loans?.map((loan) => {
                    if (loan.information_type === LOAN_INFORMATION_TYPES.SUM_LEFT) return loan.month_left || 0
                    return computeLoanMonthsLeft(loansSignatureDate, loan.period) ?? loan.month_left ?? 0
                }) || [0])
            )
        }, [quote?.loans, loansSignatureDate])

        // Calculate loan ending date
        const getLoansMaxEndingDate = useMemo(() => {
            // Changement d'assurance : fin de remboursement = aujourd'hui + durée restante
            if (isSwitchingQuote && loansMaxMonthLeft > 0) {
                const endDate = new Date()
                endDate.setMonth(endDate.getMonth() + loansMaxMonthLeft)
                return endDate
            }

            // Get start date from first loan's new_date_loan or use current date
            const firstLoan = quote?.loans?.[0]
            const startDate = parseDDMMYYYYOrISODate(firstLoan?.new_date_loan) ?? new Date()

            const endDate = new Date(startDate)
            endDate.setMonth(endDate.getMonth() + loansTotalDurationInMonths)
            return endDate
        }, [quote?.loans, loansTotalDurationInMonths, isSwitchingQuote, loansMaxMonthLeft])

        // Parse birthdate and check if borrower will be young enough at loan end
        const willBeYoungEnoughAtLoanEnd = useMemo(() => {
            // au retour des résultats l'API renvoie la date en ISO, un parse strict
            // DD/MM/YYYY rendait l'emprunteur inéligible et masquait les questions Lemoine
            const birthDate = parseDDMMYYYYOrISODate(borrowerData?.prospect_info?.birthday_date)
            if (!birthDate) return false

            // Calculate age at loan end
            const ageAtLoanEndMs = getLoansMaxEndingDate.getTime() - birthDate.getTime()
            const ageAtLoanEnd = ageAtLoanEndMs / (365.25 * 24 * 60 * 60 * 1000)

            return ageAtLoanEnd < LEMOINE_AGE_LIMIT
        }, [borrowerData?.prospect_info?.birthday_date, getLoansMaxEndingDate])

        // Calculate Lemoine eligibility (matching Symfony logic exactly)
        const isLemoineEligible = useMemo(() => {
            // 1. Check project type: must be RESIDENTIAL or RENTAL
            const hasRightType = [PROJECT_PURPOSE_RESIDENTIAL, PROJECT_PURPOSE_RENTAL].includes(projectPurpose)

            // 2. Check if borrower will be young enough at loan end (< 60 years)
            const hasValidAge = willBeYoungEnoughAtLoanEnd

            // 3. Check if insured amount (total × quotité) is under limit
            const insuredAmount = loansTotalAmount * (coverPercentage / 100)
            const isUnderAmount = loansTotalAmount > 0 && insuredAmount <= LEMOINE_AMOUNT_LIMIT

            return hasRightType && hasValidAge && isUnderAmount
        }, [projectPurpose, willBeYoungEnoughAtLoanEnd, loansTotalAmount, coverPercentage])

        // Un emprunteur éligible est Lemoine par défaut ; la question des prêts annexes ne fait que
        // rétrograder. Sync avec le watcher immediate de Symfony (LoanComparatorBorrowerGuaranteesForm.vue,
        // watch isBorrowerEligibleForLemoine) : sans cet auto-set, is_lemoine reste false sans prêt annexe.
        useEffect(() => {
            if (isLemoineEligible && getValues('has_side_loans') && getValues('is_lemoine') === false) return
            setValue('is_lemoine', isLemoineEligible)
            // Écrit aussi dans le store : la sync watch() ne capte pas toujours ce setValue (garde options +
            // timing), or le payload de soumission lit quote.main_borrower.
            const updateFn = isMainBorrower ? borrowerActions.updateMain : borrowerActions.updateCo
            updateFn({ outstanding_total_credit_under_lemoine_limit: isLemoineEligible })
        }, [isLemoineEligible, getValues, setValue, isMainBorrower, borrowerActions])

        // Calculate side loans max amount (for Lemoine question)
        const sideLoansMax = useMemo(() => {
            if (loansTotalAmount === 0) return 0
            const insuredAmount = loansTotalAmount * (coverPercentage / 100)
            return Math.max(0, LEMOINE_AMOUNT_LIMIT - insuredAmount)
        }, [loansTotalAmount, coverPercentage])

        // Dynamic Lemoine question based on sideLoansMax
        const lemoineQuestion = useMemo(() => {
            if (sideLoansMax === 0) {
                return "Avez-vous des encours assurés sur d'autres prêts immobiliers ?"
            }
            return `Le montant assuré restant de ces autres prêts est-il supérieur à ${new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
            }).format(sideLoansMax)} ?`
        }, [sideLoansMax])

        // Tooltip content for quotité
        const quotiteTooltip = `
            <p>La quotité représente le pourcentage du capital emprunté qui sera couvert par l'assurance.</p>
            <p><strong>Exemple :</strong> Pour un prêt de 100 000€, une quotité de 50% signifie que 50 000€ seront couverts.</p>
            <p>En cas de co-emprunteur, la somme des quotités doit être d'au moins 100%.</p>
        `

        return (
            <div className={`loan-comparator-borrower-guarantees-form ${className}`}>
                {/* Guarantees Alert - Dynamic message based on borrower profile and project type */}
                {formattedGuaranteesPhrase && (
                    <div className="mb-6 rounded-lg border border-green-300 bg-green-100 text-pale text-[12px]">
                        <div className="flex items-center p-5 gap-4">
                            {displayAlertImage && (
                                <div className="flex items-center justify-center shrink-0">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img src={alertImageSrc} alt="" className="max-w-[40px]" />
                                </div>
                            )}
                            <p className="m-0 text-[13.2px] font-normal text-pale leading-[1.5em]">
                                {formattedGuaranteesPhrase}
                            </p>
                        </div>
                    </div>
                )}

                {/* Lemoine eligibility section */}
                {isLemoineEligible && (
                    <>
                        <div className="mb-6">
                            <LoanFormField
                                label="Avez-vous d'autres crédits immobiliers en cours (en plus de celui-ci) ?*"
                                inline={false}
                                tooltip={{
                                    title: 'Pourquoi cette question ?',
                                    text: `A partir du 1er juin 2022, la loi Lemoine supprime le questionnaire de santé si le montant total emprunté par l'assuré ne dépasse pas ${new Intl.NumberFormat(
                                        'fr-FR',
                                        { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }
                                    ).format(
                                        LEMOINE_AMOUNT_LIMIT
                                    )} et si la fin du nouveau prêt a lieu avant le ${LEMOINE_AGE_LIMIT}ème anniversaire de l'assuré.`,
                                }}
                                tooltipId={`lemoine-tooltip-${isMainBorrower ? 'main' : 'co'}`}
                                row="switch"
                                labelId={`side-loans-label-${isMainBorrower ? 'main' : 'co'}`}
                            >
                                <Controller
                                    name="has_side_loans"
                                    control={control}
                                    render={({ field }) => (
                                        <YesNoSwitch
                                            ariaLabelledBy={`side-loans-label-${isMainBorrower ? 'main' : 'co'}`}
                                            value={!!field.value}
                                            onChange={(checked) => {
                                                field.onChange(checked)
                                                // Parité Vue toggleSideLoans : au plafond Lemoine (sideLoansMax = 0), tout encours annexe fait perdre la loi Lemoine
                                                const nextLemoine = sideLoansMax === 0 ? !checked : isLemoineEligible
                                                setValue('is_lemoine', nextLemoine)
                                                const updateFn = isMainBorrower
                                                    ? borrowerActions.updateMain
                                                    : borrowerActions.updateCo
                                                updateFn({ outstanding_total_credit_under_lemoine_limit: nextLemoine })
                                            }}
                                        />
                                    )}
                                />
                            </LoanFormField>
                        </div>

                        {/* Show second question only if has_side_loans is true AND sideLoansMax > 0 */}
                        {hasSideLoans && sideLoansMax > 0 && (
                            <div className="mb-6">
                                <LoanFormField
                                    label={`${lemoineQuestion}*`}
                                    inline={false}
                                    tooltip={{
                                        title: 'Comment calculer le montant assuré ?',
                                        text: `<ul>
                                            <li>Montant assuré = Montant emprunté × Quotité</li>
                                            <li>Exemple : Il vous reste 300 000 € d'un autre prêt à rembourser dont vous êtes couvert à hauteur de 50% (quotité). Cela correspond à un montant restant assuré de 150 000 €.<br/>300 000€ × 50% = 150 000€</li>
                                            <li>Pour profiter de la Loi Lemoine et ne pas avoir de questionnaire de santé à compléter, l'encours assuré de vos prêts en cours et de celui-ci ne doit pas être supérieur à ${new Intl.NumberFormat(
                                                'fr-FR',
                                                { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }
                                            ).format(LEMOINE_AMOUNT_LIMIT)}.</li>
                                        </ul>`,
                                    }}
                                    tooltipId={`lemoine-limit-tooltip-${isMainBorrower ? 'main' : 'co'}`}
                                    row="switch"
                                    labelId={`lemoine-label-${isMainBorrower ? 'main' : 'co'}`}
                                >
                                    {/* is_lemoine = true = encours sous le seuil → réponse « Non » (parité switch Symfony, direction par défaut) */}
                                    <Controller
                                        name="is_lemoine"
                                        control={control}
                                        render={({ field }) => (
                                            <YesNoSwitch
                                                ariaLabelledBy={`lemoine-label-${isMainBorrower ? 'main' : 'co'}`}
                                                value={!field.value}
                                                onChange={(answer) => field.onChange(!answer)}
                                            />
                                        )}
                                    />
                                </LoanFormField>
                            </div>
                        )}
                    </>
                )}

                {/* Couverture (Quotité) */}
                <div className="mb-6">
                    <LoanFormField
                        label="Quelle part du prêt souhaitez-vous couvrir ? (en %)*"
                        inline={false}
                        tooltip={{
                            title: 'Quotité',
                            text: quotiteTooltip,
                        }}
                        tooltipId={`quotite-tooltip-${isMainBorrower ? 'main' : 'co'}`}
                        error={
                            errors.cover_percentage ||
                            (externalCoverageError ? { message: externalCoverageError } : undefined)
                        }
                        htmlFor={`coverage-${isMainBorrower ? 'main' : 'co'}`}
                        imported={isImported(borrowerFieldKey(importSide, 'cover_percentage'))}
                        chatPrefill={quotityChatMark}
                    >
                        <AppFormInput
                            id={`coverage-${isMainBorrower ? 'main' : 'co'}`}
                            label=""
                            type="number"
                            min={1}
                            max={100}
                            variant="loan"
                            placeholder="Ex. 100"
                            {...register('cover_percentage', {
                                valueAsNumber: true,
                                onChange: () => clearImported(borrowerFieldKey(importSide, 'cover_percentage')),
                                onBlur: () => {
                                    syncCoverPercentageToStore()
                                    onCoverageBlur?.()
                                },
                            })}
                        />
                    </LoanFormField>
                </div>

                {/* Guarantees selection */}
                <div className="mb-6">
                    <h4 className="text-[14px] font-medium text-pale mb-3">Sélectionnez les garanties souhaitées.</h4>

                    <div className="space-y-3">
                        {[
                            ...requiredGuarantees.map((code) => ({ code, required: true })),
                            ...optionalGuarantees.map((code) => ({ code, required: false })),
                        ].map(({ code, required }) => {
                            const guarantee = guaranteesData[code]
                            if (!guarantee) return null
                            const isDisabled = required
                                ? isRequiredGuaranteeDisabled(code)
                                : isOptionalGuaranteeDisabled(code)
                            const suffix = isMainBorrower ? 'main' : 'co'

                            // Une garantie que le profil rend indisponible finit décochée : ne pas la badger
                            const guaranteeImported =
                                isImported(borrowerFieldKey(importSide, `options.${code}`)) && !!options?.[code]

                            return (
                                <div
                                    key={code}
                                    className={`relative ${guaranteeImported ? 'loan-field-imported' : ''}`}
                                >
                                    <Controller
                                        name={`options.${code}`}
                                        control={control}
                                        render={({ field }) => (
                                            <AppCheckbox
                                                value={field.value}
                                                onChange={(checked) => handleGuaranteeToggle(code, checked)}
                                                fieldName={
                                                    required ? `${code}-${suffix}` : `optional-${code}-${suffix}`
                                                }
                                                disabled={isDisabled}
                                                description={GUARANTEE_SHORT_DESCRIPTIONS[code]}
                                                tag={required ? 'Exigée par la banque' : undefined}
                                                variant="card"
                                            >
                                                {GUARANTEE_LABELS[code] ?? guarantee.name}
                                            </AppCheckbox>
                                        )}
                                    />
                                    {guaranteeImported && <LoanImportedBadge className="absolute right-4 top-4" />}
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Dependency Alerts */}
                {showDependencyAlert[GUARANTEE_IPP] && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-[14px] text-red-700 mb-4 animate-shake">
                        Vous devez sélectionner les garanties IPT/ITT pour pouvoir ajouter cette garantie
                        complémentaire. La garantie IPP ne peut être sélectionnée sans ces garanties, car elles sont
                        complémentaires.
                    </div>
                )}
                {showDependencyAlert[GUARANTEE_PLUS] && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-[14px] text-red-700 mb-4 animate-shake">
                        Vous devez sélectionner les garanties IPT/ITT pour pouvoir ajouter cette garantie
                        complémentaire. La garantie Dos/Psy ne peut être sélectionnée sans ces garanties, car elles sont
                        complémentaires.
                    </div>
                )}

                {/* Confirmation Modal for unchecking required guarantees */}
                {confirmModal.isOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCancelUncheck} />

                        {/* Modal */}
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center">
                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                                ATTENTION
                            </h3>

                            <div
                                className="text-[14px] text-gray-700 mb-6"
                                dangerouslySetInnerHTML={{
                                    __html: `<strong>Attention !</strong> Si vous enlevez <strong>${confirmModal.guaranteeName}</strong>, le contrat proposé ne sera plus équivalent à celui de <strong>${bankDenomination}</strong> et se verra refusé par celle-ci.<br><br>Souhaitez-vous tout de même l'enlever ?`,
                                }}
                            />

                            <div className="flex gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={handleCancelUncheck}
                                    className="px-4 py-2 text-[14px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    Non, garder cette option
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmUncheck}
                                    className="px-4 py-2 text-[14px] font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                                >
                                    Oui, la retirer
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
)

LoanComparatorBorrowerGuaranteesForm.displayName = 'LoanComparatorBorrowerGuaranteesForm'

export default LoanComparatorBorrowerGuaranteesForm
